import type { Signer, SignMarker } from '@/lib/signTypes';
/**
 * DataChannel-Schema für Kamera-Steuerung Berater ↔ Stele.
 * Beide Apps nutzen dieses Schema. Channel-Name: 'bamoa-cam-ctrl'
 */

import type { RoiBox, CameraCapabilities } from './cameraManager';

export type CamRole = 'main' | 'preview';

// Agent → Stele
export interface CmdRoiSet {
  type: 'camera.roi.set';
  role: CamRole;
  roi: RoiBox;
}
export interface CmdRoiMove {
  type: 'camera.roi.move';
  role: CamRole;
  dx: number;
  dy: number;
}
export interface CmdRoiZoom {
  type: 'camera.roi.zoom';
  role: CamRole;
  factor: number;
  anchor?: { x: number; y: number };
}
export interface CmdRoiReset {
  type: 'camera.roi.reset';
  role: CamRole;
}
export interface CmdScan {
  type: 'camera.scan';
  role: CamRole;
  format?: 'jpeg' | 'png' | 'webp';
}
export interface CmdKeyframe {
  type: 'camera.keyframe';
}

// Welle 8c-pre49: Dokumentenbeleuchtung über das lokale Relais (Kanal 1) der Stele.
// Der Berater schaltet, die Stele ruft lokal /Relais/setStatus?channel=1 auf.
export interface CmdLightSet {
  type: 'light.set';
  on: boolean;
}

// Welle 8d/M2: Vorschau-Stream (kleines 360p-Bild) an/aus. Der Berater kann ihn bei
// schlechter Verbindung abschalten, um die Bandbreite dem Hauptbild zu geben. Die Stele
// stoppt/startet den Preview-Sender per replaceTrack (keine Neuverhandlung).
export interface CmdPreviewSet {
  type: 'camera.preview.set';
  on: boolean;
}

/**
 * Welle 8c-pre38: Dokumenten-Signierung. PDF-basiert über die lokale SignController-API
 * der Stele (signotec/signoSign2, Signotec-Delta-Display). Der Berater lädt ein PDF
 * gechunkt zur Stele (Berater → Stele), die Stele ruft lokal POST /Sign/PostFileBase64
 * (redirect:follow → getPdfAsync blockiert bis zur Unterschrift) und schickt das signierte
 * PDF gechunkt zurück. 'sign.abort' löst auf der Stele /Sign/getkill aus.
 */
export interface CmdSignSubmitStart {
  type: 'sign.submit.start';
  sigId: string;
  filename: string;
  totalBytes: number;
  totalChunks: number;
  chunkSize: number;
  signers?: Signer[];
  markers?: SignMarker[];
  /** pre238: Gast soll vor dem Senden eine Passkey-/Biometrie-Bestätigung leisten. */
  requireIdent?: boolean;
  /** pre239: Gast soll den per SMS erhaltenen Code eingeben. */
  requireOtp?: boolean;
}
export interface CmdSignSubmitEnd {
  type: 'sign.submit.end';
  sigId: string;
}
export interface CmdSignAbort {
  type: 'sign.abort';
  sigId: string;
}
// Welle 8c-pre41: Berater bestätigt vollständigen Empfang des signierten PDFs (Agent → Stele).
export interface CmdSignResultAck {
  type: 'sign.result.ack';
  sigId: string;
}

export type CamCommand =
  | CmdRoiSet
  | CmdRoiMove
  | CmdRoiZoom
  | CmdRoiReset
  | CmdScan
  | CmdKeyframe
  | CmdLightSet
  | CmdPreviewSet
  | CmdSignSubmitStart
  | CmdSignSubmitEnd
  | CmdSignAbort
  | CmdSignResultAck;

// Stele → Agent
export interface EvtCapabilities {
  type: 'camera.capabilities';
  cameras: CameraCapabilities[];
}
export interface EvtRoiUpdated {
  type: 'camera.roi.updated';
  role: CamRole;
  cameraRole: 'webcam' | 'doccam';
  roi: RoiBox;
  effectiveZoom: number;
  isDetail: boolean;
}
/**
 * Welle 5b: Snapshot wird gechunkt übertragen damit der Main-Thread nicht blockiert
 * und der DataChannel keine >16KB-Messages bekommt.
 * Ablauf: scan.start (JSON) → N x binary ArrayBuffer-Chunks → scan.end (JSON).
 */
export interface EvtScanStart {
  type: 'camera.scan.start';
  role: CamRole;
  cameraRole: 'webcam' | 'doccam';
  format: string;
  mime?: string;
  name?: string;
  scanId: string;
  totalBytes: number;
  totalChunks: number;
  chunkSize: number;
}
export interface EvtScanEnd {
  type: 'camera.scan.end';
  scanId: string;
}
export interface EvtScanError {
  type: 'camera.scan.error';
  role: CamRole;
  error: string;
  scanId?: string;
}

// Welle 8c-pre49: Stele bestätigt den geschalteten Licht-Zustand (oder meldet Fehler).
export interface EvtLightState {
  type: 'light.state';
  on: boolean;
  ok: boolean;
  error?: string;
}

/**
 * Welle 8c-pre38: Signatur-Statusmeldungen + Rücktransport des signierten PDFs
 * (gleicher Chunk-Mechanismus wie der Scan).
 */
export interface EvtSignReceived {
  type: 'sign.received';
  sigId: string;
}
export interface EvtSignSigning {
  type: 'sign.signing';
  sigId: string;
}
export interface EvtSignResultStart {
  type: 'sign.result.start';
  sigId: string;
  filename: string;
  totalBytes: number;
  totalChunks: number;
  chunkSize: number;
  /** pre238: Identifizierungs-Nachweis (WebAuthn) + Status vom Gast. */
  identProof?: unknown;
  identStatus?: 'verified' | 'unavailable' | 'declined';
  /** pre239: vom Kunden eingegebener SMS-Code (Berater verifiziert gegen das Backend). */
  otpCode?: string;
}
export interface EvtSignResultEnd {
  type: 'sign.result.end';
  sigId: string;
}
export interface EvtSignAborted {
  type: 'sign.aborted';
  sigId: string;
}
export interface EvtSignError {
  type: 'sign.error';
  sigId: string;
  error: string;
  stage?: string;
}
// Welle 8c-pre41: Stele bestätigt vollständigen Empfang des hochgeladenen PDFs (Stele → Agent).
export interface EvtSignSubmitAck {
  type: 'sign.submit.ack';
  sigId: string;
}

export type CamEvent =
  | EvtCapabilities | EvtRoiUpdated | EvtScanStart | EvtScanEnd | EvtScanError | EvtLightState
  | EvtSignReceived | EvtSignSigning | EvtSignResultStart | EvtSignResultEnd | EvtSignAborted | EvtSignError | EvtSignSubmitAck;

export const CAM_DATACHANNEL_LABEL = 'bamoa-cam-ctrl';
export const SCAN_CHUNK_SIZE = 16 * 1024; // 16 KB
